// ROI Service - Fetches ROI Model data from /api/v2/use-case-explorer
// Maps API response to FE component structure per roi-integration.md

import { buildApiUrl } from '../config/appConfig';
import type { AssumptionGroup, AssumptionRow } from '../data/roiMockData';

export type ScenarioId = 'conservative' | 'base' | 'aggressive' | 'custom';

export interface HeaderMetrics {
    roiBase: number;
    paybackMonthsBase: number;
    net3YearValueBase: number;
    confidenceBase: 'High' | 'Medium' | 'Low';
}

export interface ScenarioCard {
    /** Annual value in dollars (not a percentage — the API name is historical). */
    roi: number;
    paybackMonths: number;
    net3YearValue: number;
    confidence: 'High' | 'Medium' | 'Low';
    /** Custom scenario only: whether assumption overrides are applied. */
    isModified?: boolean;
    /** Custom scenario only: how many assumptions differ from base. */
    modifiedCount?: number;
}

/** Narrative for the active scenario (generated at request time). */
export interface ScenarioDetails {
    description: string;
    scenarioId?: string;
    valueDrivers: string[];
    keyAssumptions: string[];
    risks: string[];
    source?: string;
    confidence?: 'High' | 'Medium' | 'Low' | string;
}

export interface AssumptionLabItem {
    conservative: number;
    base: number;
    aggressive: number;
    custom: number;
    confidence: 'High' | 'Medium' | 'Low';
    source: string;
    sourceShortLabel?: string;
}

export type AssumptionLab = Record<string, AssumptionLabItem>;

export interface SensitivityAnalysis {
    adoptionRate: number;
    constReduction: number;
    revenueUplift: number;
    efficiencyGain: number;
    implementationTime: number;
    userPopulation: number;
}

/** Absolute dollar bases that rate drivers apply to (from ROI assumptions). */
export interface RoiModelBases {
    baseRevenue: number;
    annualCostBase: number;
}

export interface FinancialBreakdown {
    baseline: number;
    benefit: number;
    costs: number;
    netvalue: number;
}

export interface ROIModelResponse {
    success: boolean;
    data: {
        headerMetrics: HeaderMetrics;
        scenarioCards: {
            conservative: ScenarioCard;
            base: ScenarioCard;
            aggressive: ScenarioCard;
            custom?: ScenarioCard;
        };
        assumptionLab: AssumptionLab;
        sensitivityAnalysis: SensitivityAnalysis;
        aiInsights: string;
        financialBreakdown: FinancialBreakdown;
        /** Per-scenario Year-1 waterfall. Prefer this over a single financialBreakdown. */
        financialBreakdownByScenario?: Partial<Record<ScenarioId, FinancialBreakdown>>;
        /** Active-scenario narrative (backward compatible). Prefer scenarioDetailsByScenario. */
        scenarioDetails?: ScenarioDetails;
        /** All four narratives from the first ROI payload — no per-scenario refetch. */
        scenarioDetailsByScenario?: Partial<Record<ScenarioId, ScenarioDetails>>;
        /**
         * Revenue / cost dollar bases for rate-driver projection (DMD-1184).
         * Revenue uplift → baseRevenue; cost reduction + efficiency → annualCostBase.
         */
        modelBases?: RoiModelBases;
    };
}

export interface ROIError {
    success: false;
    error: string;
    message: string;
}

// ─── Response normalization (camelCase, snake_case, legacy v1 shape) ───────

function isRecord(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function num(v: unknown, fallback = 0): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

function conf(v: unknown): 'High' | 'Medium' | 'Low' {
    const s = String(v ?? 'Medium');
    if (s === 'High' || s === 'Medium' || s === 'Low') return s;
    return 'Medium';
}

function normalizeModelBases(rawData: Record<string, unknown>): RoiModelBases | undefined {
    const src = rawData.modelBases ?? rawData.model_bases;
    if (!isRecord(src)) return undefined;
    const baseRevenue = num(src.baseRevenue ?? src.base_revenue, NaN);
    const annualCostBase = num(src.annualCostBase ?? src.annual_cost_base, NaN);
    if (!(baseRevenue > 0) && !(annualCostBase > 0)) return undefined;
    return {
        baseRevenue: baseRevenue > 0 ? baseRevenue : 0,
        annualCostBase: annualCostBase > 0 ? annualCostBase : 0,
    };
}

function normalizeScenarioCard(raw: unknown): ScenarioCard {
    const o = isRecord(raw) ? raw : {};
    // `annual_value` wins over `roi`: in the scenarios[] shape `roi` is a percentage,
    // while scenarioCards[].roi carries the annual dollar value the cards render.
    const annual = o.annual_value ?? o.annualValue ?? o.roi ?? o.roi_base;
    const modifiedCount = o.modifiedCount ?? o.modified_count;
    const isModified = o.isModified ?? o.is_modified;
    return {
        roi: num(annual),
        paybackMonths: num(o.paybackMonths ?? o.payback_months),
        net3YearValue: num(o.net3YearValue ?? o.net_3yr_value ?? o.value_3yr),
        confidence: conf(o.confidence),
        ...(modifiedCount != null ? { modifiedCount: num(modifiedCount) } : {}),
        ...(isModified != null ? { isModified: Boolean(isModified) } : {}),
    };
}

function strList(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    return raw.map(formatScenarioDetailItem).filter(Boolean);
}

/** API items may be strings or `{ label, value, unit, period, source, confidence }`. */
function formatScenarioDetailItem(x: unknown): string {
    if (typeof x === 'string') return x.trim();
    if (!isRecord(x)) return String(x ?? '').trim();
    const label = String(x.label ?? x.text ?? x.description ?? x.name ?? x.driver ?? x.assumption ?? x.risk ?? '').trim();
    const unit = String(x.unit ?? '').trim();
    const period = String(x.period ?? '').trim();
    const context =
        typeof x.context === 'string'
            ? x.context.trim()
            : isRecord(x.context)
                ? ''
                : String(x.context ?? '').trim();
    let valuePart = '';
    if (x.value != null && x.value !== '') {
        if (unit === '$' && typeof x.value === 'number' && Number.isFinite(x.value)) {
            const abs = Math.abs(x.value);
            valuePart =
                abs >= 1e6 ? `$${(x.value / 1e6).toFixed(1)}M`
                    : abs >= 1e3 ? `$${Math.round(x.value / 1e3)}k`
                        : `$${Math.round(x.value)}`;
        } else if (unit === '%' || unit === 'x') {
            valuePart = `${x.value}${unit}`;
        } else {
            valuePart = unit ? `${x.value} ${unit}` : String(x.value);
        }
    }
    const source = String(x.source ?? x.source_type ?? x.sourceType ?? '').trim();
    const confidence = String(x.confidence ?? x.confidence_label ?? '').trim();
    const extras = [valuePart, period, context, source, confidence].filter(Boolean);
    if (!label) return extras.join(' · ');
    if (extras.length === 0) return label;
    return `${label}: ${extras.join(' · ')}`;
}

export function normalizeScenarioDetails(raw: unknown): ScenarioDetails | undefined {
    if (!isRecord(raw)) return undefined;
    const description = String(raw.description ?? raw.summary ?? raw.narrative ?? '').trim();
    const scenarioId = String(raw.scenarioId ?? raw.scenario_id ?? '').trim() || undefined;
    const source = String(raw.source ?? '').trim() || undefined;
    const confidenceRaw = raw.confidence ?? raw.confidence_label;
    const confidence = confidenceRaw != null && String(confidenceRaw).trim() ? String(confidenceRaw).trim() : undefined;
    const details: ScenarioDetails = {
        description,
        valueDrivers: strList(raw.valueDrivers ?? raw.value_drivers),
        keyAssumptions: strList(raw.keyAssumptions ?? raw.key_assumptions),
        risks: strList(raw.risks),
        ...(scenarioId ? { scenarioId } : {}),
        ...(source ? { source } : {}),
        ...(confidence ? { confidence } : {}),
    };
    if (!details.description && details.valueDrivers.length === 0 && details.keyAssumptions.length === 0 && details.risks.length === 0) {
        return undefined;
    }
    return details;
}

const SCENARIO_IDS: ScenarioId[] = ['conservative', 'base', 'aggressive', 'custom'];

function looksLikeScenarioDetailsMap(raw: Record<string, unknown>): boolean {
    return SCENARIO_IDS.some((id) => isRecord(raw[id]));
}

function normalizeScenarioDetailsMap(raw: unknown): Partial<Record<ScenarioId, ScenarioDetails>> | undefined {
    if (!isRecord(raw) || !looksLikeScenarioDetailsMap(raw)) return undefined;
    const out: Partial<Record<ScenarioId, ScenarioDetails>> = {};
    for (const id of SCENARIO_IDS) {
        const details = normalizeScenarioDetails(raw[id]);
        if (details) out[id] = { ...details, scenarioId: details.scenarioId || id };
    }
    return Object.keys(out).length ? out : undefined;
}

export function detailsForScenario(
    data: ROIModelResponse['data'] | undefined,
    id: ScenarioId,
): ScenarioDetails | undefined {
    if (!data) return undefined;
    const fromMap = data.scenarioDetailsByScenario?.[id];
    if (fromMap) return fromMap;
    const single = data.scenarioDetails;
    if (!single) return undefined;
    if (!single.scenarioId || single.scenarioId === id) return single;
    return undefined;
}

export function narrativeFromScenarioDetails(
    details: ScenarioDetails | undefined,
    fallback: { description: string; valueDrivers: string[]; keyAssumptions: string[]; risks: string[] },
): { description: string; valueDrivers: string[]; keyAssumptions: string[]; risks: string[] } {
    if (!details) return fallback;
    return {
        description: details.description || fallback.description,
        valueDrivers: details.valueDrivers.length ? details.valueDrivers : fallback.valueDrivers,
        keyAssumptions: details.keyAssumptions.length ? details.keyAssumptions : fallback.keyAssumptions,
        risks: details.risks.length ? details.risks : fallback.risks,
    };
}

/** Compact money for scenario narrative copy ($1.0M / $754k). */
function formatNarrativeMoney(dollars: number): string {
    if (!Number.isFinite(dollars)) return '$0';
    const abs = Math.abs(dollars);
    const sign = dollars < 0 ? '-' : '';
    if (abs >= 1_000_000) {
        const m = abs / 1_000_000;
        const label = Math.abs(m - Math.round(m * 10) / 10) < 1e-9
            ? m.toFixed(1).replace(/\.0$/, '')
            : m.toFixed(1);
        return `${sign}$${label}M`;
    }
    if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
    return `${sign}$${Math.round(abs)}`;
}

/** Rate values from the lab are 0–1 fractions; show as percent points. */
function formatNarrativeRate(value: number): string {
    if (!Number.isFinite(value)) return '0%';
    const pct = value <= 1.5 ? value * 100 : value;
    const rounded = Math.round(pct * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

/**
 * DMD-1182: Custom detail panel must match the Custom summary card.
 * API `scenarioDetailsByScenario.custom` often echoes Base benefit/payback/drivers
 * even when the Custom column (and local projection) has moved — rebuild narrative
 * from the same overrides + card metrics the summary uses.
 */
export function buildCustomScenarioNarrative(
    data: ROIModelResponse['data'] | undefined,
    overrides: Record<string, number>,
    card: ScenarioCard,
    fallback: { description: string; valueDrivers: string[]; keyAssumptions: string[]; risks: string[] },
): { description: string; valueDrivers: string[]; keyAssumptions: string[]; risks: string[] } {
    const lab = data?.assumptionLab;
    const apiCustom = detailsForScenario(data, 'custom');
    const modifiedCount = card.modifiedCount
        ?? Object.entries(overrides).filter(([key, value]) => {
            const base = lab?.[key] != null ? num(lab[key].base) : NaN;
            return Number.isFinite(base) && differsFromBase(Number(value), base);
        }).length;

    const pick = (key: string): number => {
        if (Number.isFinite(overrides[key])) return Number(overrides[key]);
        if (lab?.[key]) return num(lab[key].custom ?? lab[key].base);
        return 0;
    };

    const annual = card.roi;
    const payback = card.paybackMonths;
    const revenue = pick('annualRevenueUplift');
    const costReduction = pick('costReductionTarget');
    const efficiency = pick('processEfficiencyGain');
    const riskMitigation = pick('riskMitigationValue');
    const adoption = pick('userAdoptionRate');
    const deployment = pick('timeToFullDeployment');

    const description = modifiedCount > 0
        ? `User-modified case reflecting custom assumption overrides in the lab. Annual benefit ${formatNarrativeMoney(annual)}, payback ${payback} months.`
        : `Custom column matches base case (no overrides). Annual benefit ${formatNarrativeMoney(annual)}, payback ${payback} months.`;

    const valueDrivers = [
        `Total annual benefit: ${formatNarrativeMoney(annual)}`,
        `Revenue uplift: ${formatNarrativeRate(revenue)} annually`,
        `Cost reduction: ${formatNarrativeRate(costReduction)} annually`,
        `Process efficiency gain: ${formatNarrativeRate(efficiency)}`,
        `Risk mitigation value: ${formatNarrativeMoney(riskMitigation)} / year`,
        `User adoption rate: ${formatNarrativeRate(adoption)}`,
    ];

    const keyAssumptions = [
        `User adoption reaches ${formatNarrativeRate(adoption)} by end of Year 1.`,
        `Process efficiency improves by ${formatNarrativeRate(efficiency)}.`,
        `Implementation completes in ${Math.round(deployment) || deployment} months.`,
        modifiedCount > 0
            ? `${modifiedCount} custom override${modifiedCount === 1 ? '' : 's'} applied from the assumption laboratory.`
            : 'Custom column tracks your overrides; validate against internal benchmarks.',
    ];

    const risks = apiCustom?.risks?.length
        ? apiCustom.risks
        : fallback.risks;

    return { description, valueDrivers, keyAssumptions, risks };
}

/** Format API `headerMetrics.roiBase` as a percentage label (value is used as-is, no scaling). */
export function formatHeaderRoiPercent(roiBase: number): string {
    if (!Number.isFinite(roiBase)) return '0%';
    if (Math.abs(roiBase - Math.round(roiBase)) < 1e-6) return `${Math.round(roiBase)}%`;
    return `${roiBase.toFixed(2).replace(/\.?0+$/, '')}%`;
}

function normalizeFinancialBreakdown(raw: unknown): FinancialBreakdown {
    const o = isRecord(raw) ? raw : {};
    return {
        baseline: num(o.baseline),
        benefit: num(o.benefit ?? o.benefits),
        costs: num(o.costs),
        netvalue: num(o.netvalue ?? o.net_value),
    };
}

/**
 * Year-1 waterfall for a scenario card.
 *
 * Benefits = Annual Value (`card.roi`). The API's `financialBreakdown*` often
 * embeds ~3 years of benefits (Y1 ramp + Y2 + Y3), which made the "Year 1 Impact"
 * chart disagree with Scenario Performance Overview (DMD-1185). Costs/baseline
 * still come from the API template when present.
 */
export function buildYear1FinancialBreakdown(
    card: ScenarioCard | undefined,
    costsTemplate?: Pick<FinancialBreakdown, 'baseline' | 'costs'> | null,
): FinancialBreakdown {
    const baseline = costsTemplate?.baseline ?? 0;
    const costs = costsTemplate?.costs ?? 0;
    const benefit = Math.round(card?.roi ?? 0);
    return {
        baseline,
        benefit,
        costs,
        netvalue: Math.round(baseline + benefit + costs),
    };
}

/** Year-1 waterfall for the selected scenario (aligned to that scenario's Annual Value). */
export function pickFinancialBreakdownForScenario(
    data: ROIModelResponse['data'] | undefined,
    scenario: ScenarioId,
): FinancialBreakdown {
    const empty: FinancialBreakdown = { baseline: 0, benefit: 0, costs: 0, netvalue: 0 };
    if (!data) return empty;
    const mapped = data.financialBreakdownByScenario?.[scenario];
    const fallback = data.financialBreakdown ?? empty;
    const costsTemplate = mapped ?? fallback;
    const card = data.scenarioCards?.[scenario] ?? data.scenarioCards?.base;
    if (!card) return mapped ?? fallback;
    return buildYear1FinancialBreakdown(card, costsTemplate);
}

/** Display rounding only — max 2 decimal places (0.639874 → 0.64). */
export function formatAssumptionDisplayValue(value: number, maxDecimals = 2): string {
    if (!Number.isFinite(value)) return '0';
    const rounded = Number(value.toFixed(maxDecimals));
    if (Number.isInteger(rounded)) return String(rounded);
    return rounded.toFixed(maxDecimals).replace(/0+$/, '').replace(/\.$/, '');
}

function guessLabKeyFromLabel(label: string): string | null {
    const l = label.toLowerCase();
    if (l.includes('revenue') && l.includes('uplift')) return 'annualRevenueUplift';
    if (l.includes('cost reduction')) return 'costReductionTarget';
    if (l.includes('risk mitigation')) return 'riskMitigationValue';
    if (l.includes('adoption')) return 'userAdoptionRate';
    if (l.includes('deployment') || l.includes('time to full')) return 'timeToFullDeployment';
    if (l.includes('efficiency')) return 'processEfficiencyGain';
    if (l.includes('population') || l.includes('patient')) return 'affectedPatientPopulation';
    if (l.includes('implementation cost')) return 'implementationCost';
    return null;
}

function legacyAssumptionsTableToLab(table: unknown): AssumptionLab {
    const lab: AssumptionLab = {};
    if (!Array.isArray(table)) return lab;
    for (const grp of table) {
        const g = isRecord(grp) ? grp : null;
        if (!g || !Array.isArray(g.assumptions)) continue;
        for (const row of g.assumptions) {
            const r = isRecord(row) ? row : null;
            if (!r) continue;
            const key = guessLabKeyFromLabel(String(r.label ?? ''));
            if (!key) continue;
            lab[key] = {
                conservative: num(r.conservative),
                base: num(r.base),
                aggressive: num(r.aggressive),
                custom: num(r.custom ?? r.base),
                confidence: conf(r.confidence),
                source: String(r.source ?? ''),
                sourceShortLabel: r.source_type != null ? String(r.source_type) : undefined,
            };
        }
    }
    return lab;
}

function legacySensitivityArrayToObject(arr: unknown): SensitivityAnalysis {
    const base: SensitivityAnalysis = {
        adoptionRate: 0,
        constReduction: 0,
        revenueUplift: 0,
        efficiencyGain: 0,
        implementationTime: 0,
        userPopulation: 0,
    };
    if (!Array.isArray(arr)) return base;
    for (const item of arr) {
        const it = isRecord(item) ? item : null;
        if (!it) continue;
        const label = String(it.label ?? '').toLowerCase();
        const bv = num(it.base_value);
        if (label.includes('adopt')) base.adoptionRate = bv > 1 ? bv / 100 : bv;
        else if (label.includes('cost')) base.constReduction = bv > 1 ? bv / 100 : bv;
        else if (label.includes('revenue')) base.revenueUplift = bv > 1 ? bv / 100 : bv;
        else if (label.includes('efficien')) base.efficiencyGain = bv > 1 ? bv / 100 : bv;
        else if (label.includes('implement') || label.includes('time')) base.implementationTime = bv;
        else if (label.includes('population') || label.includes('user')) base.userPopulation = bv;
    }
    return base;
}

function normalizeSensitivityObject(raw: unknown): SensitivityAnalysis {
    const o = isRecord(raw) ? raw : {};
    return {
        adoptionRate: num(o.adoptionRate ?? o.adoption_rate),
        constReduction: num(o.constReduction ?? o.cost_reduction ?? o.const_reduction),
        revenueUplift: num(o.revenueUplift ?? o.revenue_uplift),
        efficiencyGain: num(o.efficiencyGain ?? o.efficiency_gain),
        implementationTime: num(o.implementationTime ?? o.implementation_time),
        userPopulation: num(o.userPopulation ?? o.user_population),
    };
}

/**
 * Normalize any roi-model JSON (v2 camelCase, snake_case, or legacy v1) into ROIModelResponse.
 */
export function normalizeRoiModelResponse(
    body: unknown,
    opts?: { financialScenario?: ScenarioId }
): ROIModelResponse {
    const root = isRecord(body) ? body : {};
    const rawData = isRecord(root.data) ? root.data : root;
    const scenarioKey = opts?.financialScenario ?? 'base';

    // ── headerMetrics ─────────────────────────────────────────────────────
    let headerMetrics = isRecord(rawData.headerMetrics)
        ? rawData.headerMetrics
        : isRecord(rawData.header_metrics)
          ? rawData.header_metrics
          : null;

    if (!headerMetrics) {
        const legacyH = isRecord(rawData.header) ? rawData.header : null;
        if (legacyH) {
            headerMetrics = {
                roiBase: num(legacyH.roi_base ?? legacyH.roiBase),
                paybackMonthsBase: num(legacyH.payback_months ?? legacyH.paybackMonthsBase),
                net3YearValueBase: num(legacyH.net_3yr_value ?? legacyH.net3YearValueBase),
                confidenceBase: conf(legacyH.confidence ?? legacyH.confidenceBase),
            };
        } else {
            headerMetrics = {
                roiBase: 0,
                paybackMonthsBase: 0,
                net3YearValueBase: 0,
                confidenceBase: 'Medium',
            };
        }
    } else {
        headerMetrics = {
            roiBase: num(headerMetrics.roiBase ?? headerMetrics.roi_base),
            paybackMonthsBase: num(headerMetrics.paybackMonthsBase ?? headerMetrics.payback_months_base),
            net3YearValueBase: num(headerMetrics.net3YearValueBase ?? headerMetrics.net_3yr_value_base),
            confidenceBase: conf(headerMetrics.confidenceBase ?? headerMetrics.confidence_base),
        };
    }

    // ── scenarioCards ─────────────────────────────────────────────────────
    let cards = isRecord(rawData.scenarioCards)
        ? rawData.scenarioCards
        : isRecord(rawData.scenario_cards)
          ? rawData.scenario_cards
          : null;

    const scenariosById: Record<string, unknown> = {};
    if (Array.isArray(rawData.scenarios)) {
        for (const s of rawData.scenarios) {
            const o = isRecord(s) ? s : null;
            if (!o) continue;
            const id = String(o.id ?? '').toLowerCase();
            if (id) scenariosById[id] = o;
        }
    }

    if (!cards) {
        cards = {
            conservative: scenariosById.conservative,
            base: scenariosById.base,
            aggressive: scenariosById.aggressive,
            custom: scenariosById.custom,
        };
    }

    // dva-core computes the custom scenario in scenarios[]; dva-backend does not always
    // mirror it into scenarioCards. Take it from either place so Custom never shows Base.
    const customRaw = (cards && isRecord(cards) ? cards.custom : null) ?? scenariosById.custom;

    const scenarioCards = {
        conservative: normalizeScenarioCard(cards && isRecord(cards) ? cards.conservative : {}),
        base: normalizeScenarioCard(cards && isRecord(cards) ? cards.base : {}),
        aggressive: normalizeScenarioCard(cards && isRecord(cards) ? cards.aggressive : {}),
        ...(customRaw != null ? { custom: normalizeScenarioCard(customRaw) } : {}),
    };

    const byScenarioSrc = rawData.scenarioDetailsByScenario ?? rawData.scenario_details_by_scenario;
    let scenarioDetailsByScenario = normalizeScenarioDetailsMap(byScenarioSrc);

    const scenarioDetailsSrc = rawData.scenarioDetails ?? rawData.scenario_details;
    if (isRecord(scenarioDetailsSrc) && looksLikeScenarioDetailsMap(scenarioDetailsSrc) && !scenarioDetailsByScenario) {
        scenarioDetailsByScenario = normalizeScenarioDetailsMap(scenarioDetailsSrc);
    }
    const scenarioDetails =
        scenarioDetailsSrc != null && isRecord(scenarioDetailsSrc) && !looksLikeScenarioDetailsMap(scenarioDetailsSrc)
            ? normalizeScenarioDetails(scenarioDetailsSrc)
            : undefined;
    if (scenarioDetails) {
        const id = (SCENARIO_IDS.includes(scenarioDetails.scenarioId as ScenarioId)
            ? scenarioDetails.scenarioId
            : scenarioKey) as ScenarioId;
        scenarioDetailsByScenario = { [id]: scenarioDetails, ...scenarioDetailsByScenario };
    }

    // ── assumptionLab ─────────────────────────────────────────────────────
    let assumptionLab: AssumptionLab = {};
    const labRaw = rawData.assumptionLab ?? rawData.assumption_lab;
    if (isRecord(labRaw)) {
        for (const [k, v] of Object.entries(labRaw)) {
            const item = isRecord(v) ? v : null;
            if (!item) continue;
            assumptionLab[k] = {
                conservative: num(item.conservative),
                base: num(item.base),
                aggressive: num(item.aggressive),
                custom: num(item.custom ?? item.base),
                confidence: conf(item.confidence),
                source: String(item.source ?? ''),
                sourceShortLabel:
                    item.sourceShortLabel != null
                        ? String(item.sourceShortLabel)
                        : item.source_short_label != null
                          ? String(item.source_short_label)
                          : undefined,
            };
        }
    } else if (Array.isArray(rawData.assumptions_table)) {
        assumptionLab = legacyAssumptionsTableToLab(rawData.assumptions_table);
    }

    // ── sensitivityAnalysis ────────────────────────────────────────────────
    let sensitivityAnalysis: SensitivityAnalysis;
    if (Array.isArray(rawData.sensitivity_analysis)) {
        sensitivityAnalysis = legacySensitivityArrayToObject(rawData.sensitivity_analysis);
    } else {
        sensitivityAnalysis = normalizeSensitivityObject(rawData.sensitivityAnalysis ?? rawData.sensitivity_analysis);
    }

    // ── aiInsights ────────────────────────────────────────────────────────
    const aiInsights = String(
        rawData.aiInsights ?? rawData.ai_insights ?? rawData.ai_insight ?? rawData.aiInsight ?? ''
    );

    // ── financialBreakdown ─────────────────────────────────────────────────
    const fbRoot = rawData.financialBreakdown ?? rawData.financial_breakdown;
    let financialBreakdown: FinancialBreakdown = {
        baseline: 0,
        benefit: 0,
        costs: 0,
        netvalue: 0,
    };
    const fbRec = isRecord(fbRoot) ? fbRoot : null;
    if (fbRec) {
        const flat =
            'benefit' in fbRec ||
            'benefits' in fbRec ||
            'netvalue' in fbRec ||
            'net_value' in fbRec;
        const slice = flat
            ? fbRec
            : isRecord(fbRec[scenarioKey])
              ? (fbRec[scenarioKey] as Record<string, unknown>)
              : isRecord(fbRec.base)
                ? (fbRec.base as Record<string, unknown>)
                : fbRec;
        financialBreakdown = normalizeFinancialBreakdown(slice);
    }

    const fbBySrc = rawData.financialBreakdownByScenario ?? rawData.financial_breakdown_by_scenario;
    let financialBreakdownByScenario: Partial<Record<ScenarioId, FinancialBreakdown>> | undefined;
    if (isRecord(fbBySrc)) {
        financialBreakdownByScenario = {};
        for (const id of SCENARIO_IDS) {
            if (fbBySrc[id] != null) {
                financialBreakdownByScenario[id] = normalizeFinancialBreakdown(fbBySrc[id]);
            }
        }
        if (Object.keys(financialBreakdownByScenario).length === 0) {
            financialBreakdownByScenario = undefined;
        }
    }

    return {
        success: root.success !== false,
        data: {
            headerMetrics: headerMetrics as HeaderMetrics,
            scenarioCards,
            ...(scenarioDetails
                ? { scenarioDetails }
                : scenarioDetailsByScenario?.[scenarioKey]
                    ? { scenarioDetails: scenarioDetailsByScenario[scenarioKey] }
                    : scenarioDetailsByScenario?.base
                        ? { scenarioDetails: scenarioDetailsByScenario.base }
                        : {}),
            ...(scenarioDetailsByScenario ? { scenarioDetailsByScenario } : {}),
            assumptionLab,
            sensitivityAnalysis,
            aiInsights,
            financialBreakdown,
            ...(financialBreakdownByScenario ? { financialBreakdownByScenario } : {}),
            ...(() => {
                const modelBases = normalizeModelBases(rawData);
                return modelBases ? { modelBases } : {};
            })(),
        },
    };
}

// ─── Assumption Lab → UI groups (shared by ROICommandCenter & EA page) ──────

interface AssumptionConfig {
    label: string;
    groupId: string;
    groupName: string;
    groupIcon: string;
    unit: string;
    multiplier: number;
    decimals: number;
    impact: number;
    description: string;
}

const ASSUMPTION_CONFIG: Record<string, AssumptionConfig> = {
    annualRevenueUplift: {
        label: 'Annual Revenue Uplift',
        groupId: 'financial',
        groupName: 'Financial Drivers',
        groupIcon: 'DollarSign',
        unit: '%',
        multiplier: 1,
        decimals: 2,
        impact: 85,
        description:
            'Decimal fraction from API (e.g. 0.01); multiply by 100 for “percent points” (0.01 → 1.0 pp).',
    },
    costReductionTarget: {
        label: 'Cost Reduction Target',
        groupId: 'financial',
        groupName: 'Financial Drivers',
        groupIcon: 'DollarSign',
        unit: '%',
        multiplier: 1,
        decimals: 2,
        impact: 92,
        description: 'Decimal fraction from API (0–1 scale); % marks this as a rate column.',
    },
    riskMitigationValue: {
        label: 'Risk Mitigation Value',
        groupId: 'financial',
        groupName: 'Financial Drivers',
        groupIcon: 'DollarSign',
        unit: '$K',
        multiplier: 0.001,
        decimals: 0,
        impact: 68,
        description: 'Estimated value of reduced compliance and delivery risk',
    },
    userAdoptionRate: {
        label: 'User Adoption Rate',
        groupId: 'operational',
        groupName: 'Operational Drivers',
        groupIcon: 'Settings',
        unit: '%',
        multiplier: 1,
        decimals: 2,
        impact: 78,
        description: 'Decimal fraction from API (0–1); % marks this as a rate column.',
    },
    timeToFullDeployment: {
        label: 'Time to Full Deployment',
        groupId: 'operational',
        groupName: 'Operational Drivers',
        groupIcon: 'Settings',
        unit: ' mo',
        multiplier: 1,
        decimals: 1,
        impact: 65,
        description: 'Duration from kickoff to full operational capability',
    },
    processEfficiencyGain: {
        label: 'Process Efficiency Gain',
        groupId: 'operational',
        groupName: 'Operational Drivers',
        groupIcon: 'Settings',
        unit: '%',
        multiplier: 1,
        decimals: 2,
        impact: 88,
        description: 'Decimal fraction from API (0–1); % marks this as a rate column.',
    },
    affectedPatientPopulation: {
        label: 'Affected Patient Population',
        groupId: 'population',
        groupName: 'Population Drivers',
        groupIcon: 'Users',
        unit: '',
        multiplier: 1,
        decimals: 0,
        impact: 55,
        description: 'Number of patients in scope for the initiative',
    },
    implementationCost: {
        label: 'Implementation Cost',
        groupId: 'costs',
        groupName: 'Program Cost Drivers',
        groupIcon: 'Target',
        unit: '$K',
        multiplier: 0.001,
        decimals: 0,
        impact: 45,
        description: 'One-time implementation, configuration, and onboarding costs',
    },
};

function resolveSourceType(
    source: string,
    shortLabel?: string
): 'External Benchmark' | 'Historical' | 'Estimate' {
    const lc = (shortLabel ?? source).toLowerCase();
    if (lc.includes('estimate') || lc.includes('internal')) return 'Estimate';
    if (lc.includes('historical') || lc.includes('emr') || lc.includes('process')) return 'Historical';
    return 'External Benchmark';
}

export function buildAssumptionGroupsFromAssumptionLab(assumptionLab: AssumptionLab): AssumptionGroup[] {
    const groupMap = new Map<string, AssumptionGroup>();
    const ORDER = ['financial', 'operational', 'population', 'costs'];

    Object.entries(assumptionLab).forEach(([key, item]) => {
        const cfg = ASSUMPTION_CONFIG[key];
        if (!cfg) return;

        if (!groupMap.has(cfg.groupId)) {
            groupMap.set(cfg.groupId, {
                id: cfg.groupId,
                name: cfg.groupName,
                icon: cfg.groupIcon,
                expanded: cfg.groupId === 'financial' || cfg.groupId === 'operational',
                assumptions: [],
            });
        }

        const fmt = (v: number) => parseFloat((v * cfg.multiplier).toFixed(cfg.decimals));

        const row: AssumptionRow = {
            id: key,
            name: cfg.label,
            conservative: fmt(item.conservative),
            base: fmt(item.base),
            aggressive: fmt(item.aggressive),
            // Custom column edits API-scale values (fractions for % drivers, dollars for $ fields, etc.)
            custom: num(item.custom),
            baseRaw: num(item.base),
            unit: cfg.unit,
            confidence: item.confidence,
            source: item.sourceShortLabel ?? item.source,
            sourceType: resolveSourceType(item.source, item.sourceShortLabel),
            impact: cfg.impact,
            changed: Math.abs(num(item.custom) - num(item.base)) > 1e-9 * Math.max(1, Math.abs(num(item.base))),
            description: cfg.description,
        };

        groupMap.get(cfg.groupId)!.assumptions.push(row);
    });

    return ORDER.map((id) => groupMap.get(id)).filter(Boolean) as AssumptionGroup[];
}

/** Keys whose Custom value differs from base — payload for PUT custom-assumptions. */
export function collectCustomAssumptionOverrides(groups: AssumptionGroup[]): Record<string, number> {
    const overrides: Record<string, number> = {};
    for (const group of groups) {
        for (const assumption of group.assumptions) {
            if (assumption.changed && Number.isFinite(assumption.custom)) {
                overrides[assumption.id] = assumption.custom;
            }
        }
    }
    return overrides;
}

// ─── Custom scenario resolution ─────────────────────────────────────────────
//
// The API returns the stored Custom column inside `assumptionLab`, but it does not
// always return a computed `custom` scenario card (dva-backend omits it, and its
// `financialBreakdownByScenario.custom` is a copy of base). Without the projection
// below the Custom card silently renders Base numbers (DMD-1106), and edits made in
// the Assumptions Laboratory never move any metric.
//
// The projection is anchored on the base card: with an unmodified Custom column every
// output equals the base card, so it can never disagree with the server on base data.

export interface CustomScenarioResolution {
    card: ScenarioCard;
    financialBreakdown: FinancialBreakdown;
    modifiedCount: number;
    /** True when metrics were computed locally because the API returned no custom scenario. */
    isProjected: boolean;
}

/** Rate drivers that scale the annual benefit pool. */
const RATE_DRIVER_KEYS = ['annualRevenueUplift', 'costReductionTarget', 'processEfficiencyGain'] as const;

interface CustomModelInputs {
    revenueRate: number;
    costRate: number;
    efficiencyRate: number;
    riskMitigation: number;
    adoption: number;
    population: number;
    deploymentMonths: number;
    implementationCost: number;
}

function asRateFraction(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return Math.abs(n) <= 1 ? n : n / 100;
}

function readModelInputs(lab: AssumptionLab, pick: (key: string) => number): CustomModelInputs {
    return {
        revenueRate: lab.annualRevenueUplift ? asRateFraction(pick('annualRevenueUplift')) : 0,
        costRate: lab.costReductionTarget ? asRateFraction(pick('costReductionTarget')) : 0,
        efficiencyRate: lab.processEfficiencyGain ? asRateFraction(pick('processEfficiencyGain')) : 0,
        riskMitigation: lab.riskMitigationValue ? pick('riskMitigationValue') : 0,
        adoption: lab.userAdoptionRate ? asRateFraction(pick('userAdoptionRate')) : 0,
        population: lab.affectedPatientPopulation ? pick('affectedPatientPopulation') : 0,
        deploymentMonths: lab.timeToFullDeployment ? pick('timeToFullDeployment') : 0,
        implementationCost: lab.implementationCost ? pick('implementationCost') : 0,
    };
}

/**
 * Parse compact USD from value-driver copy, e.g. "($5.8M)" or "($339k)".
 * Used only when the API omits modelBases.
 */
function parseUsdCompactToken(text: string): number | null {
    const m = text.match(/\(\$([0-9]+(?:\.[0-9]+)?)\s*([KMB])?\)/i)
        ?? text.match(/\$([0-9]+(?:\.[0-9]+)?)\s*([KMB])\b/i);
    if (!m) return null;
    const n = Number(m[1]);
    if (!Number.isFinite(n)) return null;
    const unit = (m[2] || '').toUpperCase();
    const mult = unit === 'B' ? 1e9 : unit === 'M' ? 1e6 : unit === 'K' ? 1e3 : 1;
    return n * mult;
}

/**
 * Infer BR / ACB from scenario narrative lines when modelBases is absent
 * (older API). Matches backend scenarioDollarImpact: dollars = base × rate × adoption.
 */
function inferModelBasesFromNarrative(
    data: ROIModelResponse['data'],
    base: CustomModelInputs,
): RoiModelBases | undefined {
    const lines = [
        ...(data.scenarioDetails?.valueDrivers ?? []),
        ...(data.scenarioDetailsByScenario?.base?.valueDrivers ?? []),
    ];
    if (lines.length === 0 || !(base.adoption > 0)) return undefined;

    let revDollars: number | null = null;
    let costDollars: number | null = null;
    for (const line of lines) {
        const lower = line.toLowerCase();
        const dollars = parseUsdCompactToken(line);
        if (dollars == null) continue;
        if (lower.includes('revenue uplift')) revDollars = dollars;
        else if (lower.includes('cost reduction')) costDollars = dollars;
    }
    if (revDollars == null && costDollars == null) return undefined;

    const baseRevenue =
        revDollars != null && base.revenueRate > 0
            ? revDollars / (base.revenueRate * base.adoption)
            : 0;
    const annualCostBase =
        costDollars != null && base.costRate > 0
            ? costDollars / (base.costRate * base.adoption)
            : 0;
    if (!(baseRevenue > 0) && !(annualCostBase > 0)) return undefined;
    return { baseRevenue, annualCostBase };
}

function resolveModelBases(
    data: ROIModelResponse['data'],
    base: CustomModelInputs,
): RoiModelBases | undefined {
    const fromApi = data.modelBases;
    if (fromApi && (fromApi.baseRevenue > 0 || fromApi.annualCostBase > 0)) {
        return fromApi;
    }
    return inferModelBasesFromNarrative(data, base);
}

/**
 * Rate-driven dollar pool (pre-adoption), mirroring backend scenarioDollarImpact.
 * User population scales the patient-driven cost opportunity; revenue is org-level
 * so Adoption vs Population sensitivities diverge even when risk mitigation is $0.
 */
function ratePoolBeforeAdoption(
    bases: RoiModelBases,
    inputs: CustomModelInputs,
    populationRatio: number,
): number {
    const pop = Number.isFinite(populationRatio) && populationRatio > 0 ? populationRatio : 1;
    return (
        bases.baseRevenue * inputs.revenueRate
        + pop * bases.annualCostBase * (inputs.costRate + inputs.efficiencyRate)
    );
}

/** Ratio that degrades to a no-op when the base value can't scale. */
function scaleRatio(baseValue: number, customValue: number): number {
    return baseValue > 0 && Number.isFinite(customValue) ? customValue / baseValue : 1;
}

function differsFromBase(custom: number, base: number): boolean {
    return Math.abs(custom - base) > 1e-9 * Math.max(1, Math.abs(base));
}

function projectCustomScenario(
    data: ROIModelResponse['data'],
    overrides: Record<string, number>,
): CustomScenarioResolution | undefined {
    const lab = data.assumptionLab;
    const baseCard = data.scenarioCards?.base;
    if (!lab || Object.keys(lab).length === 0 || !baseCard) return undefined;

    const annualBase = baseCard.roi;
    if (!(annualBase > 0)) return undefined;

    const modifiedCount = Object.entries(overrides).filter(
        ([key, value]) => lab[key] != null && differsFromBase(Number(value), num(lab[key].base)),
    ).length;

    const baseBreakdown = data.financialBreakdownByScenario?.base ?? data.financialBreakdown;

    // An unmodified Custom column is the base case — return the base card with a
    // Year-1 waterfall so Benefits stay aligned to Annual Value (DMD-1185).
    if (modifiedCount === 0) {
        return {
            card: { ...baseCard, isModified: false, modifiedCount: 0 },
            financialBreakdown: buildYear1FinancialBreakdown(baseCard, baseBreakdown),
            modifiedCount: 0,
            isProjected: true,
        };
    }

    const base = readModelInputs(lab, (key) => num(lab[key]?.base));
    const custom = readModelInputs(lab, (key) => {
        const override = overrides[key];
        return Number.isFinite(override) ? Number(override) : num(lab[key]?.base);
    });

    const bases = resolveModelBases(data, base);
    let annualCustom: number;

    if (bases && base.adoption > 0) {
        // Annual benefit ∝ adoption × (revenue pool + population-scaled cost pool + risk).
        // Anchored so an unmodified column reproduces the base card (DMD-1184).
        const poolBase = ratePoolBeforeAdoption(bases, base, 1);
        const poolCustom = ratePoolBeforeAdoption(
            bases,
            custom,
            scaleRatio(base.population, custom.population),
        );
        const grossBase = base.adoption * (poolBase + base.riskMitigation);
        const grossCustom = Math.max(
            0,
            (custom.adoption > 0 ? custom.adoption : base.adoption)
                * (poolCustom + custom.riskMitigation),
        );
        annualCustom = annualBase * scaleRatio(grossBase, grossCustom);
    } else {
        // Legacy fallback when dollar bases are unavailable: sum rates (equal %
        // rates still look identical). Population scales only the rate-driven
        // share so it can differ from adoption when risk mitigation ≠ 0.
        const rateSum = (i: CustomModelInputs) => i.revenueRate + i.costRate + i.efficiencyRate;
        const unitBase = base.adoption > 0 ? annualBase / base.adoption : annualBase;
        const baseRates = rateSum(base);
        const rateUnitBase = Math.max(0, unitBase - base.riskMitigation);
        const rateUnitCustom = baseRates > 0
            ? rateUnitBase * (rateSum(custom) / baseRates)
            : rateUnitBase;
        const popRatio = scaleRatio(base.population, custom.population);
        const unitCustom =
            rateUnitCustom * popRatio + custom.riskMitigation;
        annualCustom = annualBase
            * scaleRatio(unitBase, unitCustom)
            * scaleRatio(base.adoption, custom.adoption);
    }

    if (!(annualCustom > 0)) return undefined;

    const baseCosts = Math.abs(baseBreakdown?.costs ?? 0);
    const totalCostBase = baseCosts > 0
        ? baseCosts
        : Math.max(0, annualBase * 3 - baseCard.net3YearValue);
    const costFactor = scaleRatio(base.implementationCost, custom.implementationCost);
    const totalCostCustom = totalCostBase * costFactor;

    // A longer rollout earns fewer benefit-years inside the same 3-year window.
    const deploymentDeltaYears = (custom.deploymentMonths - base.deploymentMonths) / 12;
    const benefitYearsBase = (baseCard.net3YearValue + totalCostBase) / annualBase;
    const benefitYearsCustom = Math.max(0, benefitYearsBase - deploymentDeltaYears);

    const paybackMonths = baseCard.paybackMonths > 0
        ? Math.max(
            1,
            Math.round(
                baseCard.paybackMonths * costFactor * (annualBase / annualCustom)
                + (custom.deploymentMonths - base.deploymentMonths),
            ),
        )
        : baseCard.paybackMonths;

    const costs = Math.round(((baseBreakdown?.costs ?? -1) <= 0 ? -1 : 1) * totalCostCustom);
    const card: ScenarioCard = {
        roi: annualCustom,
        paybackMonths,
        net3YearValue: annualCustom * benefitYearsCustom - totalCostCustom,
        confidence: baseCard.confidence,
        isModified: modifiedCount > 0,
        modifiedCount,
    };

    return {
        card,
        // Year 1 Benefits = Custom Annual Value (not multi-year API benefit × scale).
        financialBreakdown: buildYear1FinancialBreakdown(card, {
            baseline: baseBreakdown?.baseline ?? 0,
            costs,
        }),
        modifiedCount,
        isProjected: true,
    };
}

/** Overrides implied by the Custom column the API returned. */
function labCustomOverrides(lab: AssumptionLab): Record<string, number> {
    const stored: Record<string, number> = {};
    for (const [key, item] of Object.entries(lab)) {
        if (differsFromBase(num(item.custom), num(item.base))) stored[key] = num(item.custom);
    }
    return stored;
}

function sameOverrides(a: Record<string, number>, b: Record<string, number>): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
        if (a[key] == null || b[key] == null) return false;
        if (differsFromBase(a[key], b[key])) return false;
    }
    return true;
}

/**
 * Metrics for the Custom scenario card, waterfall, and "N Modified" badge.
 * Prefers the API's computed custom scenario, and falls back to a local projection
 * whenever the API has none or the Custom column has moved past what it describes.
 *
 * @param overrides Current Custom column values keyed by assumption id
 *                  (see `collectCustomAssumptionOverrides`).
 */
export function resolveCustomScenario(
    data: ROIModelResponse['data'] | undefined,
    overrides: Record<string, number>,
): CustomScenarioResolution | undefined {
    if (!data) return undefined;

    const projection = projectCustomScenario(data, overrides);
    const serverCard = data.scenarioCards?.custom;

    if (serverCard && sameOverrides(labCustomOverrides(data.assumptionLab ?? {}), overrides)) {
        const costsTemplate =
            projection?.financialBreakdown
            ?? data.financialBreakdownByScenario?.custom
            ?? data.financialBreakdown;
        return {
            card: serverCard,
            // Prefer Year-1 Benefits from the Custom Annual Value — API custom
            // breakdown is often a copy of Base's multi-year benefits (DMD-1185).
            financialBreakdown: buildYear1FinancialBreakdown(serverCard, costsTemplate),
            modifiedCount: serverCard.modifiedCount ?? Object.keys(overrides).length,
            isProjected: false,
        };
    }

    return projection;
}

/** Recharts / waterfall-style bar rows from API financial breakdown */
export function buildWaterfallBarsFromFinancial(breakdown: FinancialBreakdown) {
    const fmtK = (v: number): string => {
        const abs = Math.abs(v);
        const sign = v < 0 ? '-' : v > 0 ? '+' : '';
        if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
        if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
        return `${sign}$${abs.toFixed(0)}`;
    };
    const toChart = (v: number) => v / 1_000;
    return [
        { name: 'Baseline', value: 0, displayValue: '$0', type: 'baseline', fill: '#71717a' },
        { name: 'Benefits', value: toChart(breakdown.benefit), displayValue: fmtK(breakdown.benefit), type: 'benefit', fill: '#10b981' },
        { name: 'Costs', value: toChart(breakdown.costs), displayValue: fmtK(breakdown.costs), type: 'cost', fill: '#ef4444' },
        { name: 'Net Value', value: toChart(breakdown.netvalue), displayValue: fmtK(breakdown.netvalue), type: 'net', fill: '#FF6E4C' },
    ];
}

const SENSITIVITY_VARIANCE = 0.2;

const SENSITIVITY_MAP: Array<{ key: string; name: string }> = [
    { key: 'userAdoptionRate', name: 'Adoption Rate' },
    { key: 'costReductionTarget', name: 'Cost Reduction' },
    { key: 'annualRevenueUplift', name: 'Revenue Uplift' },
    { key: 'processEfficiencyGain', name: 'Efficiency Gain' },
    { key: 'timeToFullDeployment', name: 'Implementation Time' },
    { key: 'affectedPatientPopulation', name: 'User Population' },
    { key: 'riskMitigationValue', name: 'Risk Mitigation' },
];

function fmtSensitivityImpactK(v: number): string {
    const abs = Math.abs(v);
    if (abs >= 1000) return `$${(abs / 1000).toFixed(1)}M`;
    return `$${abs}K`;
}

/**
 * Ranked sensitivity rows: each assumption is varied ±20% around the current
 * Custom column (falling back to Base), and impact is the resulting Δ Net
 * 3-Year Value from the same projection used for the Custom scenario card.
 *
 * Projection applies revenue uplift to baseRevenue and cost/efficiency to
 * annualCostBase, and scales only the cost-side pool by user population
 * (DMD-1184) — so equal percentage rates no longer produce identical bars.
 */
export function buildSensitivityRankingFromLab(
    data: ROIModelResponse['data'],
    overrides: Record<string, number> = {},
) {
    const lab = data.assumptionLab;
    if (!lab || Object.keys(lab).length === 0) return [];

    const baseline = resolveCustomScenario(data, overrides);
    const baselineNet =
        baseline?.card.net3YearValue
        ?? data.scenarioCards?.base?.net3YearValue
        ?? data.headerMetrics?.net3YearValueBase
        ?? 0;

    type ScoredItem = {
        key: string;
        name: string;
        negative: number;
        positive: number;
        totalImpact: number;
    };

    const scored: ScoredItem[] = [];

    for (const { key, name } of SENSITIVITY_MAP) {
        const item = lab[key];
        if (!item) continue;

        const current = Number.isFinite(overrides[key]) ? Number(overrides[key]) : num(item.base);
        if (!(Math.abs(current) > 0)) continue;

        const up = resolveCustomScenario(data, { ...overrides, [key]: current * (1 + SENSITIVITY_VARIANCE) });
        const down = resolveCustomScenario(data, { ...overrides, [key]: current * (1 - SENSITIVITY_VARIANCE) });
        if (!up || !down) continue;

        const deltaUp = up.card.net3YearValue - baselineNet;
        const deltaDown = down.card.net3YearValue - baselineNet;
        // Tornado: right = upside (favorable Δ), left = downside (unfavorable Δ).
        // Deployment time etc. can invert which variance direction helps.
        const positive = Math.round(Math.max(deltaUp, deltaDown, 0) / 1_000);
        const negative = Math.round(Math.min(deltaUp, deltaDown, 0) / 1_000);
        const totalImpact = Math.abs(positive) + Math.abs(negative);
        if (totalImpact === 0) continue;

        scored.push({ key, name, negative, positive, totalImpact });
    }

    scored.sort((a, b) => b.totalImpact - a.totalImpact);

    return scored.map((item, idx) => ({
        name: item.name,
        assumptionKey: item.key,
        negative: item.negative,
        positive: item.positive,
        rank: idx + 1,
        label: `+${fmtSensitivityImpactK(item.positive)} / -${fmtSensitivityImpactK(Math.abs(item.negative))}`,
        impactExplanation: `A ±${SENSITIVITY_VARIANCE * 100}% change in ${item.name} moves Net Value by +${fmtSensitivityImpactK(item.positive)} / -${fmtSensitivityImpactK(Math.abs(item.negative))}. This variable has the #${idx + 1} strongest impact on Net Value.`,
    }));
}

/**
 * Fetch ROI Model data for a use case
 * @param clientId - Client ID
 * @param useCaseId - Use case ID
 * @param scenario - Scenario type (default: 'base')
 * @returns ROI Model data or error
 */
export async function fetchROIModel(
    clientId: string,
    useCaseId: string | number,
    scenario: ScenarioId = 'base'
): Promise<ROIModelResponse> {
    const effectiveClientId = '300';
    const url = buildApiUrl(
        `/api/v2/use-case-explorer/${encodeURIComponent(effectiveClientId)}/opportunities/${encodeURIComponent(useCaseId)}/roi-model?scenario=${scenario}`
    );

    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || `Failed to fetch ROI model (${response.status})`);
    }

    return normalizeRoiModelResponse(data, { financialScenario: scenario });
}

/**
 * Export ROI Model data as CSV
 * @param clientId - Client ID
 * @param useCaseId - Use case ID
 * @returns CSV file download
 */
export async function exportROIModelCSV(
    clientId: string,
    useCaseId: string | number
): Promise<void> {
    const effectiveClientId = '300';
    const url = buildApiUrl(
        `/api/v2/use-case-explorer/${encodeURIComponent(effectiveClientId)}/opportunities/${encodeURIComponent(useCaseId)}/roi-model/export`
    );

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to export ROI model (${response.status})`);
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || 'roi-model.csv'
        : 'roi-model.csv';

    const blob = await response.blob();
    const url_obj = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url_obj;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url_obj);
    document.body.removeChild(a);
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number): string {
    if (!Number.isFinite(value)) return '$0';
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
}

/**
 * Format percentage
 */
export function formatPercent(value: number): string {
    if (!Number.isFinite(value)) return '0%';
    return `${Math.round(value)}%`;
}

/**
 * Format payback period
 */
export function formatPayback(months: number | null): string {
    if (months === null || !Number.isFinite(months)) return 'TBD';
    return `${Math.round(months)} mo`;
}
